/**
 * @file use-service-request.ts
 * @module app/modules/service-request/hooks/use-service-request
 * @description State for the service request modal: access, scope, tab, request list, counts, and
 * the request actions (raise, approve, decline, withdraw).
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  approveServiceRequest,
  createServiceRequest,
  declineServiceRequest,
  getServiceRequestAccess,
  getServiceRequestCounts,
  listServiceRequests,
  withdrawServiceRequest,
} from '../services/service-request.service';
import {
  CreateServiceRequestPayload,
  ServiceRequestAccess,
  ServiceRequestActionDefinition,
  ServiceRequestCounts,
  ServiceRequest,
  ServiceRequestScope,
  ServiceRequestTab,
} from '../types/service-request.types';
import { useLiveRefresh } from './use-live-refresh';
import { getHistorySeenAt, markHistorySeen } from '../utils/history-seen';

const EMPTY_COUNTS: ServiceRequestCounts = {
  minePending: 0,
  approvalsPending: 0,
  newDecisions: 0,
};
const NO_ACCESS: ServiceRequestAccess = { requestable: [], approvable: [] };

const toMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

/**
 * Loads access first, then requests, while the modal is open; reloads when the scope or tab
 * changes, and when the server says a service request changed (socket). Responses that
 * arrive after a newer load started are ignored.
 */
export const useServiceRequest = (open: boolean, userId: string) => {
  // null until this opening's access check finishes; lists wait for it.
  const [access, setAccess] = useState<ServiceRequestAccess | null>(null);
  // When History was last seen before this visit; rows decided after it are marked new.
  const [historySeenBefore, setHistorySeenBefore] = useState<string | null>(
    null
  );
  const [scope, setScope] = useState<ServiceRequestScope>('mine');
  const [tab, setTab] = useState<ServiceRequestTab>('pending');
  const [records, setRecords] = useState<ServiceRequest[]>([]);
  const [counts, setCounts] = useState<ServiceRequestCounts>(EMPTY_COUNTS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const loadToken = useRef(0);
  // Rows on screen belong to this scope/tab; another view's rows are cleared, not dimmed.
  const loadedView = useRef('');

  const refresh = useCallback(async () => {
    if (!access) return;
    const token = ++loadToken.current;
    const view = `${scope}:${tab}`;
    if (loadedView.current !== view) {
      loadedView.current = '';
      setRecords([]);
    }
    setLoading(true);
    setError(null);
    try {
      const [list, nextCounts] = await Promise.all([
        listServiceRequests({ scope, tab }),
        getServiceRequestCounts(userId ? getHistorySeenAt(userId) : undefined),
      ]);
      if (token !== loadToken.current) return;
      loadedView.current = view;
      setRecords(list.records);
      setCounts(nextCounts);
    } catch (loadError) {
      if (token !== loadToken.current) return;
      setRecords([]);
      setError(
        toMessage(loadError, 'Unable to load requests. Please try again.')
      );
    } finally {
      if (token === loadToken.current) setLoading(false);
    }
  }, [access, scope, tab, userId]);

  // Opening my History marks decisions as seen. Declared before the load effect below so the
  // counts request already uses the new time.
  useEffect(() => {
    if (!open || !userId || scope !== 'mine' || tab !== 'history') return;
    setHistorySeenBefore(markHistorySeen(userId));
  }, [open, userId, scope, tab]);

  // Live reload while open: socket event, tab focus, or a timer when there is no socket.
  useLiveRefresh(() => void refresh(), open);

  useEffect(() => {
    if (!open) {
      setAccess(null);
      return;
    }
    let cancelled = false;
    getServiceRequestAccess()
      .then((next) => {
        if (cancelled) return;
        // Someone who can only approve has nothing under My requests, so start on Approvals.
        if (!next.requestable.length && next.approvable.length) {
          setScope('approvals');
        }
        setAccess(next);
      })
      .catch(() => {
        if (!cancelled) setAccess(NO_ACCESS);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const canApprove = (access?.approvable.length ?? 0) > 0;

  /** Every action the user can see, by key, so request rows can render their inputs. */
  const actionsByKey = useMemo(() => {
    const map = new Map<string, ServiceRequestActionDefinition>();
    const { requestable, approvable } = access ?? NO_ACCESS;
    [...requestable, ...approvable].forEach((action) =>
      map.set(action.key, action)
    );
    return map;
  }, [access]);

  useEffect(() => {
    if (open) void refresh();
  }, [open, refresh]);

  useEffect(() => {
    if (access && !canApprove && scope === 'approvals') setScope('mine');
  }, [access, canApprove, scope]);

  const createRequest = useCallback(
    (body: CreateServiceRequestPayload, warehouseId?: string) =>
      createServiceRequest(body, warehouseId),
    []
  );

  /** Runs an approve/decline/withdraw call for one row, then reloads the list. */
  const act = useCallback(
    async (request: ServiceRequest, call: () => Promise<ServiceRequest>) => {
      setBusyId(request._id);
      try {
        return await call();
      } finally {
        setBusyId(null);
        void refresh();
      }
    },
    [refresh]
  );

  const approveRequest = useCallback(
    (request: ServiceRequest) =>
      act(request, () => approveServiceRequest(request._id)),
    [act]
  );

  const declineRequest = useCallback(
    (request: ServiceRequest, comment: string) =>
      act(request, () => declineServiceRequest(request._id, comment)),
    [act]
  );

  const withdrawRequest = useCallback(
    (request: ServiceRequest, reason: string) =>
      act(request, () => withdrawServiceRequest(request._id, reason)),
    [act]
  );

  return {
    requestableActions: access?.requestable ?? NO_ACCESS.requestable,
    actionsByKey,
    canApprove,
    scope,
    setScope,
    tab,
    setTab,
    records,
    counts,
    /** Mark rows decided after this time as new (only on my History tab). */
    newSince: scope === 'mine' && tab === 'history' ? historySeenBefore : null,
    loading: loading || (open && !access),
    error,
    busyId,
    refresh,
    createRequest,
    approveRequest,
    declineRequest,
    withdrawRequest,
  };
};
